import type {
	MemberAttributeAddressType,
	MemberAttributeArchType,
	MemberAttributeType,
} from "../types/memberAttribute.types";

// Configured via PROTECTED_MEMBER_ATTRIBUTE_ARCH_TYPES env var (comma-separated, case-insensitive).
// Frontend: same var exposed to browser bundle via vite.config.ts `define` block.
export const PROTECTED_MEMBER_ATTRIBUTE_ARCH_TYPES = new Set(
	(process.env["PROTECTED_MEMBER_ATTRIBUTE_ARCH_TYPES"] ?? "name,furigana")
		.split(",")
		.map((s) => s.trim().toLowerCase())
		.filter(Boolean),
);

// Configured via PROTECTED_MEMBER_ATTRIBUTE_IDS env var (comma-separated integers).
// Fallback for rows where archType is null.
export const PROTECTED_MEMBER_ATTRIBUTE_IDS = new Set(
	(process.env["PROTECTED_MEMBER_ATTRIBUTE_IDS"] ?? "1,2")
		.split(",")
		.map((s) => Number(s.trim()))
		.filter((n) => !Number.isNaN(n)),
);

export const isProtectedMemberAttribute = (memberAttribute: {
	memberAttributeId?: number | null;
	archType?: unknown;
}) =>
	(typeof memberAttribute.archType === "string" &&
		PROTECTED_MEMBER_ATTRIBUTE_ARCH_TYPES.has(
			memberAttribute.archType.toLowerCase(),
		)) ||
	(memberAttribute.memberAttributeId != null &&
		PROTECTED_MEMBER_ATTRIBUTE_IDS.has(memberAttribute.memberAttributeId));

export const MEMBER_ATTRIBUTE_FILE_TYPES = Object.freeze([
	"image",
	"video",
	"audio",
	"file",
]);

export const MEMBER_ATTRIBUTE_ADDRESS_TYPES = Object.freeze([
	"address",
	"address_postal",
	"address_prefecture",
	"address_city",
	"address_address",
	"address_building",
] as (keyof MemberAttributeAddressType)[]);

export const memberAttributeArchTypeFromType: Record<
	MemberAttributeType,
	MemberAttributeArchType
> = Object.freeze({
	text: "string",
	textarea: "string",
	color: "string",
	url: "string",
	number_integer: "number",
	number_float: "number",
	date: "date",
	time: "date",
	datetime: "date",
	boolean: "choice",
	select: "choice",
	radio: "choice",
	checkbox: "choice",
	file: "file",
	image: "file",
	video: "file",
	audio: "file",
	email: "contact",
	telephone: "contact",
	address: "address",
	address_postal: "address",
	address_prefecture: "address",
	address_city: "address",
	address_address: "address",
	address_building: "address",
	firstName: "name",
	lastName: "name",
	firstNameKana: "name",
	lastNameKana: "name",
	fullName: "name",
	fullNameKana: "name",
});

export const MEMBER_ATTRIBUTE_CONSTANTS = {
	/**
	 * Supported attribute types
	 */
	TYPES: {
		TEXT: "text" as const,
		NUMBER: "number" as const,
		DATE: "date" as const,
		SELECT: "select" as const,
		BOOLEAN: "boolean" as const,
	},

	/**
	 * System limits
	 */
	LIMITS: {
		MAX_ATTRIBUTES_PER_MEMBER: 50,
		MAX_ATTRIBUTE_NAME_LENGTH: 100,
		MAX_ATTRIBUTE_VALUE_LENGTH: 1000,
		MAX_ATTRIBUTE_LABEL_LENGTH: 255,
		MAX_ATTRIBUTE_DESCRIPTION_LENGTH: 500,
		MAX_CHOICES_PER_ATTRIBUTE: 100,
		MAX_CHOICE_VALUE_LENGTH: 255,
		MAX_CHOICE_LABEL_LENGTH: 255,
		MAX_BULK_UPDATE_MEMBERS: 1000,
	},

	/**
	 * Default values for new attributes
	 */
	DEFAULTS: {
		TYPE: "text" as const,
		REQUIRED: false,
		SHOW_IN_PROFILE: true,
		SHOW_IN_LIST: false,
		SHOW_IN_EXPORT: true,
		IS_FILTERABLE: false,
		IS_SORTABLE: false,
		IS_ACTIVE: true,
		DISPLAY_ORDER: 0,
	},

	/**
	 * Validation patterns and rules
	 */
	VALIDATION: {
		// Attribute name must be alphanumeric with underscores and hyphens
		NAME_PATTERN: /^[a-zA-Z0-9_-]+$/,

		// Reserved attribute names that cannot be used
		RESERVED_NAMES: [
			"id",
			"memberId",
			"createdAt",
			"updatedAt",
			"deletedAt",
			"lineId",
			"displayName",
			"picUrl",
			"statusMessage",
			"language",
			"memberSince",
			"activeUntil",
			"isRegistered",
			"isFriends",
			"isBlocked",
			"remarks",
			"kakeruPoint",
			"countVisit",
			"lastVisit",
			"unreadCount",
		],

		// Date format patterns
		DATE_FORMATS: {
			ISO: "YYYY-MM-DD",
			DISPLAY: "YYYY年M月D日",
			DATETIME: "YYYY-MM-DD HH:mm:ss",
			DATETIME_DISPLAY: "YYYY年M月D日 HH:mm",
		},
	},

	/**
	 * Display configuration
	 */
	DISPLAY: {
		// Default column widths in pixels
		COLUMN_WIDTHS: {
			TEXT: 200,
			NUMBER: 120,
			DATE: 150,
			BOOLEAN: 100,
			SELECT: 180,
			MULTISELECT: 220,
		},

		// Default sort priorities
		SORT_PRIORITIES: {
			REQUIRED: 1,
			PROFILE: 2,
			LIST: 3,
			EXPORT: 4,
			OTHER: 5,
		},
	},

	/**
	 * API rate limits
	 */
	RATE_LIMITS: {
		BULK_UPDATE_PER_MINUTE: 10,
		EXPORT_PER_HOUR: 100,
		DEFINITION_CHANGES_PER_HOUR: 50,
	},

	/**
	 * Cache configuration
	 */
	CACHE: {
		DEFINITIONS_TTL: 300, // 5 minutes in seconds
		CHOICES_TTL: 600, // 10 minutes in seconds
		MEMBER_ATTRIBUTES_TTL: 60, // 1 minute in seconds
	},

	/**
	 * Error messages
	 */
	ERRORS: {
		ATTRIBUTE_EXISTS: "An attribute with this name already exists",
		ATTRIBUTE_NOT_FOUND: "Attribute definition not found",
		MEMBER_NOT_FOUND: "Member not found",
		INVALID_TYPE: "Invalid attribute type",
		INVALID_VALUE: "Invalid value for attribute type",
		REQUIRED_MISSING: "Required attribute is missing",
		NAME_RESERVED: "This attribute name is reserved",
		NAME_INVALID: "Attribute name contains invalid characters",
		CHOICE_EXISTS: "A choice with this value already exists",
		CHOICE_NOT_FOUND: "Choice not found",
		CANNOT_DELETE_IN_USE: "Cannot delete attribute that is in use",
		BULK_UPDATE_LIMIT: "Bulk update limit exceeded",
		VALIDATION_FAILED: "Attribute validation failed",
	},

	/**
	 * Success messages
	 */
	MESSAGES: {
		DEFINITIONS_RETRIEVED: "Attribute definitions retrieved successfully",
		ATTRIBUTES_RETRIEVED: "Member attributes retrieved successfully",
		ATTRIBUTES_UPDATED: "Member attributes updated successfully",
		DEFINITION_CREATED: "Attribute definition created successfully",
		DEFINITION_UPDATED: "Attribute definition updated successfully",
		DEFINITION_DELETED: "Attribute definition deleted successfully",
		CHOICE_CREATED: "Attribute choice created successfully",
		CHOICE_UPDATED: "Attribute choice updated successfully",
		CHOICE_DELETED: "Attribute choice deleted successfully",
		CHOICES_RETRIEVED: "Attribute choices retrieved successfully",
		BULK_UPDATE_COMPLETE: "Bulk attribute update completed",
	},

	/**
	 * Special attribute sections/groups
	 */
	SECTIONS: {
		BASIC: "基本情報",
		CONTACT: "連絡先",
		PREFERENCES: "設定",
		CUSTOM: "カスタム",
		SYSTEM: "システム",
		METADATA: "メタデータ",
	},

	/**
	 * Attribute type icons (for UI)
	 */
	TYPE_ICONS: {
		TEXT: "📝",
		NUMBER: "🔢",
		DATE: "📅",
		SELECT: "📋",
		MULTISELECT: "☑️",
		BOOLEAN: "✅",
	},
} as const;

// Member attribute type labels for UI display
export const memberAttributeLabels: Record<string, Record<string, string>> = {
	string: {
		text: "テキスト",
		textarea: "テキストエリア",
		color: "カラー",
		url: "URL",
		email: "メールアドレス",
		telephone: "電話番号",
		firstName: "名前（名）",
		lastName: "名前（姓）",
		firstNameKana: "名前（名・カナ）",
		lastNameKana: "名前（姓・カナ）",
		fullName: "氏名",
		fullNameKana: "氏名（カナ）",
	},
	number: {
		number_integer: "数値（整数）",
		number_float: "数値（小数）",
	},
	boolean: {
		boolean: "真偽値",
	},
	select: {
		select: "セレクト",
		radio: "ラジオボタン",
		checkbox: "チェックボックス",
	},
	date: {
		date: "日付",
		time: "時間",
		datetime: "日時",
	},
	file: {
		file: "ファイル",
		image: "画像",
		video: "動画",
		audio: "音声",
	},
	address: {
		address_postal: "住所（郵便番号）",
		address_prefecture: "住所（都道府県）",
		address_city: "住所（市区町村）",
		address_address: "住所（町名番地）",
		address_building: "住所（建物名）",
	},
};

// Flattened memberAttributeLabels object into a single object
export const FLAT_MEMBER_ATTRIBUTE_LABELS = Object.values(
	memberAttributeLabels,
).reduce((accum, curr) => ({ ...accum, ...curr }), {});
